<script>
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';

  import { TargetingService } from '@client/services/targeting';

  import { PriorityChangeForm } from '../index.js';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let originalTargeting = null;

  const getTargetingGroups = async () => {
    const targeting = await TargetingService.get();
    originalTargeting = JSON.parse(JSON.stringify(targeting));

    const departments = targeting.departments || [];

    return departments.map((dept, index) => ({
      ...dept,
      priority: index + 1,
      label: getGroupLabel(dept),
      value: dept._id || `dept_${index}`
    }));
  };

  const getGroupLabel = (dept) => {
    const parts = [];

    if (dept.targettype) {
      parts.push(dept.targettype);
    }

    if (dept.sendergroups?.length) {
      parts.push(`${dept.sendergroups.length} group${dept.sendergroups.length !== 1 ? 's' : ''}`);
    }

    if (dept.ads?.length) {
      parts.push(`${dept.ads.length} campaign${dept.ads.length !== 1 ? 's' : ''}`);
    }

    if (dept.tags?.length) {
      parts.push(`${dept.tags.length} tag${dept.tags.length !== 1 ? 's' : ''}`);
    }

    return parts.length > 0 ? parts.join(' • ') : 'Targeting Group';
  };

  const saveTargetingGroupOrder = async (items) => {
    const reorderedDepartments = items.map(({ priority, label, value, _id, ...rest }) => rest);

    const updatedTargeting = {
      accounts: originalTargeting.accounts || [],
      departments: reorderedDepartments
    };

    await TargetingService.update(updatedTargeting);

    step.data = {
      items,
      rollbackData: {
        originalTargeting
      }
    };
  };

  const rollbackPriorityChange = async () => {
    if (step.data?.rollbackData?.originalTargeting) {
      try {
        const response = await TargetingService.update(step.data.rollbackData.originalTargeting);

        if (response?.status >= 400) {
          notify($format('label.FAILURE.ROLLBACK_TARGETING_GROUP_PRIORITY'), 'error');
        } else {
          notify($format('label.SUCCESS.ROLLBACK_TARGETING_GROUP_PRIORITY'));
        }
      } catch (error) {
        console.error('Failed to rollback targeting group priority:', error);
        notify($format('label.FAILURE.ROLLBACK_TARGETING_GROUP_PRIORITY'), 'error');
      }
    }
  };
</script>

<PriorityChangeForm
  bind:nextDisabled
  bind:step
  {onExecute}
  {onRollback}
  getFunction={getTargetingGroups}
  saveFunction={saveTargetingGroupOrder}
  rollbackFunction={rollbackPriorityChange}
  itemLabel={$format('label.TARGETING_GROUP')}
>
  <div slot="item" let:item>
    <div class="route-content">
      <div class="route-section">
        <b>{$format('label.TARGET')}</b>
        <div class="blockname">
          {item.targettype === 'sender' ? $format('label.SENDER') : $format('label.RECIPIENT')}
        </div>
      </div>

      {#if item.sendergroups?.length}
        <div class="route-section">
          <b>{$format('label.GROUPS')}</b>
          <div class="items">
            {#each item.sendergroups as group}
              <div class="item">{group.groupname}</div>
            {/each}
          </div>
        </div>
      {/if}

      {#if item.ads?.length}
        <div class="route-section">
          <b>{$format('label.CAMPAIGNS')}</b>
          <div class="items">
            {#each item.ads as campaign}
              <div class="item">{campaign.text}</div>
            {/each}
          </div>
        </div>
      {/if}

      {#if item.tags?.length}
        <div class="route-section">
          <b>{$format('label.TAGS')}</b>
          <div class="items">
            {#each item.tags as tag}
              <div class="item">{tag.text}</div>
            {/each}
          </div>
        </div>
      {/if}
    </div>
  </div>
</PriorityChangeForm>

<style lang="scss">
  .route-content {
    align-items: center;
    display: grid;
    gap: 24px;
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }

  .route-section {
    > b {
      display: block;
      font-size: var(--font-size-medium);
      margin-bottom: var(--margin-base);
    }

    .blockname {
      color: var(--color-text);
      display: inline-flex;
      font-size: var(--font-size-small);
    }

    .items {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: 4px;

      .item {
        border: 1px solid var(--border-color);
        border-radius: var(--border-radius-base);
        color: var(--color-gray-700);
        display: inline-flex;
        font-size: var(--font-size-small);
        padding: var(--padding-small) var(--padding-base);
      }
    }
  }
</style>
